import {
	Button,
	ChipConfirmModal,
	ChipInput,
	ChipModal,
	ChipModalBody,
	ChipModalField,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	EmptyState,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { Search } from "@ondit/ui/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import type { DistributionChannel, PublishingPlatform } from "~/core/api/distribution";
import { ApiError, api } from "~/core/api/index";
import { publishingTerms, publishingTerm } from "~/core/channel-directory";
import * as fmt from "~/core/format";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { PlatformIdentity } from "~/modules/analytics/components/platform-identity";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import {
	byValue,
	CATEGORIES,
	INDUSTRIES,
	matches,
	PlatformValue,
	TIERS,
} from "~/modules/analytics/features/platform-publishing/platforms";

const STATUSES = ["distributed", "pending", "none"] as const;
const CADENCES = ["once", "ongoing"] as const;

type Row = { platform: PublishingPlatform; channel: DistributionChannel };

/**
 * Every channel, and what has gone out on each.
 *
 * Progress lists the work by article; this lists it by where it went, which is
 * the question "where have we not been yet" needs. A launch site or a
 * directory is submitted to once, so it carries its own submitted mark rather
 * than waiting for an article that will never be sent there.
 */
export function ChannelsPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution-channels"],
		queryFn: () => api.distributionChannels(entityId),
		...read,
	});
	const [search, setSearch] = useState("");
	const [statuses, setStatuses] = useState<string[]>([]);
	const [cadences, setCadences] = useState<string[]>([]);
	const [categories, setCategories] = useState<string[]>([]);
	const [industries, setIndustries] = useState<string[]>([]);
	const [tiers, setTiers] = useState<string[]>([]);

	return (
		<StageShell
			entityId={entityId}
			reading="channels"
			lede={t("channels.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async state={state}>
				{(data) => {
					const byId = new Map(data.channels.map((c) => [c.id, c]));
					const all: Row[] = data.platforms.flatMap((platform) => {
						const channel = byId.get(platform.id);
						return channel ? [{ platform, channel }] : [];
					});
					const once = all.filter((r) => r.platform.cadence === "once");
					const ongoing = all.filter((r) => r.platform.cadence !== "once");
					const done = (rows: Row[]) => rows.filter((r) => r.channel.status === "distributed").length;
					const rows = all
						.filter(
							({ platform: p, channel: c }) =>
								`${p.name} ${p.domain}`.toLowerCase().includes(search.toLowerCase()) &&
								(statuses.length === 0 || statuses.includes(c.status)) &&
								(cadences.length === 0 || cadences.includes(p.cadence ?? "ongoing")) &&
								matches(p, { categories, industries, tiers }),
						)
						.sort((a, b) => byValue(a.platform, b.platform));
					return (
						<>
							<div className="stats insight-stats">
								<Stat label={t("channels.stat.channels")} value={`${done(all)} / ${all.length}`} />
								<Stat label={t("channels.stat.once")} value={`${done(once)} / ${once.length}`} />
								<Stat label={t("channels.stat.ongoing")} value={`${done(ongoing)} / ${ongoing.length}`} />
								<Stat
									label={t("channels.stat.items")}
									value={fmt.number(
										locale,
										all.reduce((n, r) => n + r.channel.published, 0),
									)}
								/>
							</div>
							<div className="flex flex-wrap items-center gap-(--s-2)">
								<ChipInput
									className="w-[calc(var(--s-14)*3.5)] max-w-full"
									icon={Search}
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									placeholder={t("distribution.search")}
									aria-label={t("distribution.search")}
								/>
								<ChipSelect
									multiSelect
									showAllOption
									allOptionLabel={t("channels.status")}
									multiSelectValues={statuses}
									onMultiSelectChange={setStatuses}
									aria-label={t("channels.status")}
									options={STATUSES.map((s) => ({ value: s, label: t(`channels.status.${s}`) }))}
								/>
								<ChipSelect
									multiSelect
									showAllOption
									allOptionLabel={t("channels.cadence")}
									multiSelectValues={cadences}
									onMultiSelectChange={setCadences}
									aria-label={t("channels.cadence")}
									options={CADENCES.map((c) => ({ value: c, label: t(`channels.cadence.${c}`) }))}
								/>
								<ChipSelect
									multiSelect
									showAllOption
									allOptionLabel={t("publishing.category")}
									multiSelectValues={categories}
									onMultiSelectChange={setCategories}
									aria-label={t("publishing.category")}
									options={publishingTerms("categories").map((term) => ({
										value: term.id,
										label: pick(locale, term.label),
									}))}
								/>
								<ChipSelect
									multiSelect
									showAllOption
									allOptionLabel={t("publishing.industry")}
									multiSelectValues={industries}
									onMultiSelectChange={setIndustries}
									aria-label={t("publishing.industry")}
									options={publishingTerms("industries").map((term) => ({
										value: term.id,
										label: pick(locale, term.label),
									}))}
								/>
								<ChipSelect
									multiSelect
									showAllOption
									allOptionLabel={t("publishing.value")}
									multiSelectValues={tiers}
									onMultiSelectChange={setTiers}
									aria-label={t("publishing.value")}
									options={publishingTerms("tiers").map((term) => ({
										value: term.id,
										label: pick(locale, term.label),
									}))}
								/>
							</div>
							{rows.length === 0 ? (
								<EmptyState size="compact" title={t("channels.empty")} />
							) : (
								<div className="table-scroll">
									<Table variant="resource">
										<TableHeader>
											<TableRow>
												<TableHead className="wrap">{t("distribution.platform")}</TableHead>
												<TableHead>{t("publishing.value")}</TableHead>
												<TableHead>{t("channels.cadence")}</TableHead>
												<TableHead className="num">{t("channels.published")}</TableHead>
												<TableHead className="num">{t("channels.pending")}</TableHead>
												<TableHead>{t("channels.last")}</TableHead>
												<TableHead>{t("channels.status")}</TableHead>
												<TableHead />
											</TableRow>
										</TableHeader>
										<TableBody>
											{rows.map((row) => (
												<ChannelRow key={row.platform.id} entityId={entityId} row={row} />
											))}
										</TableBody>
									</Table>
								</div>
							)}
						</>
					);
				}}
			</Async>
		</StageShell>
	);
}

function ChannelRow({ entityId, row: { platform: p, channel: c } }: { entityId: string; row: Row }) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<TableRow>
			<TableCell className="wrap">
				<a href={p.url} target="_blank" rel="noopener noreferrer">
					<PlatformIdentity platform={p} />
				</a>
				<div className="faint">
					{[
						...(p.categories ?? []).map((k) => publishingTerm("categories", k, locale)),
						p.languages?.length
							? p.languages.map((l) => fmt.language(locale, l)).join(" / ")
							: t("publishing.language.any"),
					].join(" · ")}
				</div>
				{p.summary && <div className="faint">{pick(locale, p.summary)}</div>}
			</TableCell>
			<TableCell>
				<PlatformValue platform={p} stacked />
			</TableCell>
			<TableCell>{t(`channels.cadence.${p.cadence ?? "ongoing"}`)}</TableCell>
			<TableCell className="num">{fmt.number(locale, c.published)}</TableCell>
			<TableCell className="num">{fmt.number(locale, c.pending)}</TableCell>
			<TableCell>{c.last_published_at ? fmt.date(locale, c.last_published_at) : "—"}</TableCell>
			<TableCell>
				{c.listing?.url ? (
					<a href={c.listing.url} target="_blank" rel="noopener noreferrer">
						{t("channels.submitted")}
					</a>
				) : c.listing ? (
					t("channels.submitted")
				) : (
					t(`channels.status.${c.status}`)
				)}
			</TableCell>
			<TableCell>
				{p.cadence === "once" &&
					(c.listing ? <Withdraw entityId={entityId} platform={p} /> : <Submit entityId={entityId} platform={p} />)}
			</TableCell>
		</TableRow>
	);
}

function useListingMutation<T>(entityId: string, fn: (value: T) => Promise<unknown>) {
	const { locale } = useI18n();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: fn,
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
		onError: (error) => toast.error(error instanceof ApiError ? error.localized(locale) : String(error)),
	});
}

function Submit({ entityId, platform }: { entityId: string; platform: PublishingPlatform }) {
	const t = useT();
	const { editor, why } = useAccess();
	const [open, setOpen] = useState(false);
	const [url, setUrl] = useState("");
	const submit = useListingMutation(entityId, (value: string) => api.submitListing(entityId, platform.id, value));
	return (
		<>
			<Button
				size="sm"
				variant="outline"
				disabled={!editor}
				title={whyDisabled(editor, why.editor)}
				onClick={() => setOpen(true)}
			>
				{t("channels.markSubmitted")}
			</Button>
			<ChipModal open={open} onOpenChange={setOpen} dismissDisabled={submit.isPending} srTitle={platform.name}>
				<ChipModalHeader onClose={() => setOpen(false)} closeAriaLabel={t("common.close")}>
					{t("channels.markSubmitted")} · {platform.name}
				</ChipModalHeader>
				<ChipModalBody>
					<ChipModalField
						type="input"
						inputType="url"
						title={t("channels.listingUrl")}
						hint={t("channels.listingHint")}
						value={url}
						onChange={setUrl}
						placeholder={platform.url}
					/>
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("common.cancel")}
					onCancel={() => setOpen(false)}
					primaryAction={{
						label: t("channels.markSubmitted"),
						disabled: submit.isPending,
						onClick: () =>
							submit.mutate(url, {
								onSuccess: () => {
									setOpen(false);
									setUrl("");
								},
							}),
					}}
				/>
			</ChipModal>
		</>
	);
}

function Withdraw({ entityId, platform }: { entityId: string; platform: PublishingPlatform }) {
	const t = useT();
	const { editor, why } = useAccess();
	const [open, setOpen] = useState(false);
	const withdraw = useListingMutation(entityId, () => api.withdrawListing(entityId, platform.id));
	return (
		<>
			<Button
				size="sm"
				variant="ghost"
				disabled={!editor}
				title={whyDisabled(editor, why.editor)}
				onClick={() => setOpen(true)}
			>
				{t("channels.withdraw")}
			</Button>
			<ChipConfirmModal
				open={open}
				onOpenChange={setOpen}
				title={t("channels.withdrawTitle")}
				text={t("channels.withdrawBody", { platform: platform.name })}
				dismissLabel={t("common.cancel")}
				confirm={{
					label: t("channels.withdraw"),
					variant: "destructive",
					onClick: () => withdraw.mutate(undefined, { onSuccess: () => setOpen(false) }),
				}}
			/>
		</>
	);
}
